import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Buildings, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CommercialCleaningPage() {
  const navigate = useNavigate();
  const [premisesType, setPremisesType] = useState("");
  const [address, setAddress] = useState("");
  const [desiredDate, setDesiredDate] = useState("");
  const [needs, setNeeds] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientSurname, setClientSurname] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = clientName && clientEmail && clientPhone && address;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/property/request`, {
        service_type: "commercial", client_name: clientName, client_surname: clientSurname,
        client_email: clientEmail, client_phone: clientPhone, address,
        desired_date: desiredDate, commercial_premises_type: premisesType,
        commercial_needs: needs, comments,
      });
      if (data.success) { setSubmitted(true); toast.success("Demande envoyée !"); }
    } catch { toast.error("Erreur"); } finally { setSubmitting(false); }
  };

  if (submitted) return (
    <div className="min-h-[60vh] bg-[#EDEBE4] flex items-center justify-center px-6" data-testid="commercial-success">
      <div className="max-w-md text-center">
        <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">Demande envoyée</h1>
        <p className="text-[#1A1A1A]/60 text-sm mb-6">Nous vous recontacterons pour établir un devis personnalisé pour vos locaux.</p>
        <button onClick={() => navigate("/property-services")} className="bg-[#1A1A1A] text-white px-6 py-3 text-xs tracking-[0.15em] uppercase">Retour</button>
      </div>
    </div>
  );

  return (
    <>
      <SEO title="Nettoyage Locaux Commerciaux — ALTURA Property Services" description="Nettoyage professionnel de bureaux, commerces et espaces professionnels. Sur devis." />
      <section className="bg-[#1A1A1A] py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-6">
          <button onClick={() => navigate("/property-services")} className="text-white/50 text-xs flex items-center gap-2 mb-6 hover:text-white"><ArrowLeft size={12} /> Property Services</button>
          <h1 className="font-heading text-2xl sm:text-3xl text-white tracking-wide" data-testid="commercial-title">Locaux Commerciaux</h1>
          <p className="text-white/50 text-sm mt-3">Bureaux, commerces, espaces professionnels — intervention sur devis personnalisé.</p>
        </div>
      </section>
      <section className="bg-[#EDEBE4] py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white border border-[#1A1A1A]/10 p-6 space-y-5">
            <div>
              <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Type de locaux</label>
              <input type="text" value={premisesType} onChange={e => setPremisesType(e.target.value)}
                placeholder="Bureau, commerce, restaurant, entrepôt..."
                className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none" data-testid="premises-input" />
            </div>
            <div>
              <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Adresse *</label>
              <input type="text" value={address} onChange={e => setAddress(e.target.value)}
                placeholder="Adresse complète des locaux"
                className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none" data-testid="address-input" />
            </div>
            <div>
              <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Date souhaitée</label>
              <input type="date" value={desiredDate} onChange={e => setDesiredDate(e.target.value)}
                className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" />
            </div>
            <div>
              <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Décrivez vos besoins</label>
              <textarea value={needs} onChange={e => setNeeds(e.target.value)} rows={3}
                placeholder="Surface, fréquence souhaitée, besoins spécifiques..."
                className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none resize-none" data-testid="needs-input" />
            </div>
            <hr className="border-[#D0CEC7]" />
            <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide">Vos coordonnées</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Prénom *</label><input type="text" value={clientName} onChange={e => setClientName(e.target.value)} className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" data-testid="name-input" /></div>
              <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Nom</label><input type="text" value={clientSurname} onChange={e => setClientSurname(e.target.value)} className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" /></div>
            </div>
            <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Email *</label><input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" data-testid="email-input" /></div>
            <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Téléphone *</label><input type="tel" value={clientPhone} onChange={e => setClientPhone(e.target.value)} className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none" data-testid="phone-input" /></div>
            <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Commentaires</label><textarea value={comments} onChange={e => setComments(e.target.value)} rows={2} placeholder="Informations complémentaires..." className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent outline-none resize-none" /></div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button onClick={handleSubmit} disabled={!canSubmit || submitting}
                className={`flex-1 py-3 text-xs uppercase tracking-[0.15em] transition-colors ${canSubmit ? "bg-[#1A1A1A] text-white hover:bg-[#333]" : "bg-[#1A1A1A]/20 text-[#1A1A1A]/40 cursor-not-allowed"}`}
                data-testid="submit-btn">
                {submitting ? "Envoi..." : "Envoyer ma demande"}
              </button>
              <a href="https://wa.me/33681969342" target="_blank" rel="noopener noreferrer"
                className="flex-1 py-3 text-center border border-[#1A1A1A]/20 text-[#1A1A1A] text-xs uppercase tracking-[0.15em] hover:border-[#1A1A1A]/50 transition-colors"
                data-testid="whatsapp-btn">
                Nous contacter sur WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
